import { FC, ReactNode, useMemo, useState } from 'react';
import { FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { ProjectsGridNew } from '../../../components/grids';
import styled, { useTheme } from 'styled-components';
import { AboutTextWrapper } from '../styles';
import { LayoutConst } from 'core';
import { NoProjectsContent } from './NoProjectsContent';
import { LinksItems } from "../../../hooks/useServiceLinks";
import { ProjectControllerWrapper } from 'shared/pages/explore/styles';
import { CloseIcon } from 'shared/icons';
import { useTranslation } from 'react-i18next';

export interface DesignerLayoutProps {
    isLoading?: boolean;
    isLoadingContent?: boolean;
    isHiddenStatus?: boolean;
    isMe?: boolean;
    avatar?: ReactNode;
    subscribe?: ReactNode;
    isAlumni?: ReactNode;
    title?: ReactNode;
    more?: ReactNode;
    buttons?: ReactNode;
    ctaBlock?: ReactNode;
    status?: ReactNode;
    description?: ReactNode;
    channelInfo?: ReactNode;
    networks?: ReactNode;
    social?: ReactNode;
    line?: ReactNode;
    mergeAccount?: string;
    filterBar?: ReactNode;
    content?: ReactNode;
    folders?: ReactNode;
    cloudTags?: ReactNode;
    sortLine?: ReactNode;
    canFeedback: boolean;
    designer: any;
    minMargin?: boolean;
    isAlumniUser?: boolean;
    noProjects?: any;
    serviceLinks?: LinksItems;
}

const ContentHolder = styled(FlexBox)`
  .infinite-scroll-component  {
    overflow: visible !important;
  }

  .checkboxes {
    * {
        font-size: 15px;
        line-height: 20px;
    }
  }
`

const AvatarContainer = styled.div`
  min-width: 270px;
  width: 270px;
  height: 270px;
  margin-right: 24px;
//   margin-bottom: 24px;
  position: relative;

    @media (max-width: ${LayoutConst.screen.l}px) {
    max-width: 160px;
    max-height: 160px;
    min-width: 160px;
    min-height: 160px;
  }
`;

const CloseButton = styled.span`
  cursor: pointer;

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.cookies.icon.hoverFill};
      transition: fill 0.2s;
    }
  }

  svg path {
    fill: ${({ theme }) => theme.cookies.icon.fill};
    transition: fill 0.2s;
  }

  @media (max-width: 743px) {
    top: 3px;
    right: 10px;
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const MergeAccountsMsgWrapper = styled.div`
    display: flex;
    padding: 8px 24px;
    gap: 8px;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    border-radius: 3px;
    background: #F2F2F2;
    color: #151514;
    font-family: Inter;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 13px;
    margin-bottom: 16px;

    svg {
        height: 16px;
        width: 16px;
    }

    #merge-button {
        color: #151514;
        display: inline;
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: auto;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
        cursor: pointer;
    }
`

export const DesignerLayout: FC<DesignerLayoutProps> = (props) => {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);
    const { t } = useTranslation();
    const isTab = useBreakpoint(1200)

    const [showMerge, setShowMerge] = useState(true)

    const shouldVisibleNoProjectsContent = useMemo(
        () => (
            !props.isMe &&
            props.isLoadingContent !== undefined &&
            !props.isLoadingContent &&
            !!props.noProjects?.all) &&
            !props.isLoading
        , [
            props.isLoadingContent,
            props.noProjects,
            props.isLoading,
            props.isMe
        ])

    // console.log('servisLinks',props.serviceLinks)

    if (shouldVisibleNoProjectsContent) {
        return <NoProjectsContent
            content={props.content}
            designer={props.designer}
            serviceLinks={props.serviceLinks}
        />
    }

    if (isMobile && props.isLoading) {
        return <FlexBox direction={'column'} directionMobile={'column'} width={'100%'} gyMobile={16}>
            <FlexBox gxMobile={10}>
                <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width={'calc(50vw - 15px)'}
                    height={'calc(50vw - 15px)'}
                />
                <FlexBox gyMobile={4} directionMobile={'column'} >
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={'calc(50vw - 15px)'}
                        height={'7vw'}
                    />
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={'calc(25vw - 15px)'}
                        height={'7vw'}
                    />
                </FlexBox>
            </FlexBox>
            <Skeleton
                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                width={'calc(83vw - 10px)'}
                height={'4.3vw'}
            />
            <FlexBox direction={'column'} offsetTop={8} width={'100%'}>
                <ProjectsGridNew isLoading={true} />
            </FlexBox>
        </FlexBox>
    }

    return (
        <FlexBox direction={'column'} directionMobile={'column'} width={'100%'}>
            {showMerge && props.mergeAccount && <MergeAccountsMsgWrapper>
                <span>
                    {t('designer_edit.merge_accounts_message')} <a href={props.mergeAccount} id="merge-button">{t('designer_edit.merge_accounts_here')}</a>. 
                    {' '}
                    {t('designer_edit.merge_accounts_later')}
                </span>
                <CloseButton onClick={() => setShowMerge(false)}>
                    <CloseIcon />
                </CloseButton>
            </MergeAccountsMsgWrapper>}
            <FlexBox className='bfr-cta'>
                <AvatarContainer>
                    {props.isLoading ? (
                        <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={270}
                            height={270}
                        />
                    ) : (
                        props.avatar
                    )}
                    {props.more}
                    {props.isLoading ? (
                        !isMobile && <FlexBox offsetTopMobile={16}>
                        </FlexBox>
                    ) : (
                        props.isAlumni
                    )}
                </AvatarContainer>
                {(!props.isLoading || !isMobile) && <FlexBox direction={'column'} width={'100%'} style={{ minHeight: is743? '270px' : 'auto', position: 'relative' }}>
                    <FlexBox style={{ minHeight: is743 ? '50px' : 'auto', position: 'relative' }} align={'flex-start'} justify={'space-between'}>
                        <FlexBox justify={'space-between'} justifyMobile='space-between' width={'100%'}>
                            {props.isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={294}
                                    height={50}
                                />
                            ) : (
                                props.title
                            )}
                            {props.buttons}
                        </FlexBox>
                        <FlexBox style={{ position: 'absolute', top: 0, right: 0 }}>
                            {props.isLoading ? (
                                <FlexBox gx={8}>
                                </FlexBox>
                            ) : props.social
                            }
                        </FlexBox>
                    </FlexBox>
                    {!props.isHiddenStatus && (
                        <FlexBox align={'center'} offsetTop={8}>
                            {props.isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={270}
                                    height={22}
                                />
                            ) : (
                                props.status
                            )}
                        </FlexBox>
                    )}
                    {props.isLoading || props.isLoadingContent ? null : <FlexBox>{props?.subscribe}</FlexBox>}
                    {!isTab && <AboutTextWrapper isLoading={props.isLoading}>
                        {props.isLoading ? (
                            <FlexBox direction={'column'} width={'100%'}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={'100%'}
                                    height={112}
                                />
                                <FlexBox gx={24} offsetTop={32}>
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                </FlexBox>
                            </FlexBox>
                        ) : (
                            props.description
                        )}
                    </AboutTextWrapper>}
                    {props.isLoading ? (
                        <FlexBox>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={'100%'}
                                height={112}
                            />
                        </FlexBox>
                    ) : (
                        props.channelInfo
                    )}
                    {<FlexBox align={'flex-end'} justify={'flex-start'} width={'100%'} height={'100%'}>
                        <FlexBox offsetTop={0} width="100%">
                            {props.isLoading ? (
                                <FlexBox gx={24}>
                                </FlexBox>
                            ) : (
                                <FlexBox className='designer-layout_netwroks' offsetTop={8} align='center' width="100%">
                                    {props.networks}
                                </FlexBox>
                            )}
                        </FlexBox>

                    </FlexBox>}
                </FlexBox>}


            </FlexBox>

            {isTab &&
                <AboutTextWrapper isLoading={props.isLoading}>
                    {props.isLoading ? (
                        <FlexBox direction={'column'} width={'100%'}>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={'100%'}
                                height={112}
                            />
                            <FlexBox gx={24} offsetTop={32}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                            </FlexBox>
                        </FlexBox>
                    ) : (
                        props.description
                    )}
                </AboutTextWrapper>
            }

            {props.ctaBlock}

            <FlexBox direction={'column'} width={'100%'} offsetTop={24}>
                <FlexBox width={'100%'} offsetTop={0}>
                    {props.isLoading ? (
                        <FlexBox offsetBottom={24} justify={'space-between'} width={'100%'}>
                            <FlexBox gx={32}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                            </FlexBox>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={115}
                                height={38}
                            />
                        </FlexBox>
                    ) : (
                        props.line
                    )}
                </FlexBox>
                {!props?.noProjects?.tab && (
                    <>
                        <FlexBox width={'100%'}>{props.filterBar}</FlexBox>
                        <FlexBox width={'100%'}>{props.folders}</FlexBox>
                        <FlexBox width={'100%'} offsetBottom={props.isLoadingContent ? 24 : 0}>{props.cloudTags}</FlexBox>
                    </>
                )}
                <ContentHolder className='designer-layout_content-holder' direction={'column'} width={'100%'}>
                    {!props.isLoading && props.sortLine}
                    {props.isLoading || props.isLoadingContent && <ProjectsGridNew isLoading />}
                    {!props.isLoadingContent && (
                        <ProjectControllerWrapper>
                            {props.content}
                        </ProjectControllerWrapper>
                    )}
                </ContentHolder>
            </FlexBox>

        </FlexBox>
    );
};
import { FC, ReactNode, useMemo, useState } from 'react';
import { FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { ProjectsGridNew } from '../../../components/grids';
import styled, { useTheme } from 'styled-components';
import { AboutTextWrapper } from '../styles';
import { LayoutConst } from 'core';
import { NoProjectsContent } from './NoProjectsContent';
import { LinksItems } from "../../../hooks/useServiceLinks";
import { ProjectControllerWrapper } from 'shared/pages/explore/styles';
import { CloseIcon } from 'shared/icons';
import { useTranslation } from 'react-i18next';

export interface DesignerLayoutProps {
    isLoading?: boolean;
    isLoadingContent?: boolean;
    isHiddenStatus?: boolean;
    isMe?: boolean;
    avatar?: ReactNode;
    subscribe?: ReactNode;
    isAlumni?: ReactNode;
    title?: ReactNode;
    more?: ReactNode;
    buttons?: ReactNode;
    ctaBlock?: ReactNode;
    status?: ReactNode;
    description?: ReactNode;
    channelInfo?: ReactNode;
    networks?: ReactNode;
    social?: ReactNode;
    line?: ReactNode;
    mergeAccount?: string;
    filterBar?: ReactNode;
    content?: ReactNode;
    folders?: ReactNode;
    cloudTags?: ReactNode;
    sortLine?: ReactNode;
    canFeedback: boolean;
    designer: any;
    minMargin?: boolean;
    isAlumniUser?: boolean;
    noProjects?: any;
    serviceLinks?: LinksItems;
}

const ContentHolder = styled(FlexBox)`
  .infinite-scroll-component  {
    overflow: visible !important;
  }

  .checkboxes {
    * {
        font-size: 15px;
        line-height: 20px;
    }
  }
`

const AvatarContainer = styled.div`
  min-width: 270px;
  width: 270px;
  height: 270px;
  margin-right: 24px;
//   margin-bottom: 24px;
  position: relative;

    @media (max-width: ${LayoutConst.screen.l}px) {
    max-width: 160px;
    max-height: 160px;
    min-width: 160px;
    min-height: 160px;
  }
`;

const CloseButton = styled.span`
  cursor: pointer;

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.cookies.icon.hoverFill};
      transition: fill 0.2s;
    }
  }

  svg path {
    fill: ${({ theme }) => theme.cookies.icon.fill};
    transition: fill 0.2s;
  }

  @media (max-width: 743px) {
    top: 3px;
    right: 10px;
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const MergeAccountsMsgWrapper = styled.div`
    display: flex;
    padding: 8px 24px;
    gap: 8px;
    box-sizing: border-box;
    height: 100%;
    width: 100%;
    border-radius: 3px;
    background: #F2F2F2;
    color: #151514;
    font-family: Inter;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 13px;
    margin-bottom: 16px;

    svg {
        height: 16px;
        width: 16px;
    }

    #merge-button {
        color: #151514;
        display: inline;
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: auto;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
        cursor: pointer;
    }
`

export const DesignerLayout: FC<DesignerLayoutProps> = (props) => {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);
    const { t } = useTranslation();
    const isTab = useBreakpoint(1200)

    const [showMerge, setShowMerge] = useState(true)

    const shouldVisibleNoProjectsContent = useMemo(
        () => (
            !props.isMe &&
            props.isLoadingContent !== undefined &&
            !props.isLoadingContent &&
            !!props.noProjects?.all) &&
            !props.isLoading
        , [
            props.isLoadingContent,
            props.noProjects,
            props.isLoading,
            props.isMe
        ])

    // console.log('servisLinks',props.serviceLinks)

    if (shouldVisibleNoProjectsContent) {
        return <NoProjectsContent
            content={props.content}
            designer={props.designer}
            serviceLinks={props.serviceLinks}
        />
    }

    if (isMobile && props.isLoading) {
        return <FlexBox direction={'column'} directionMobile={'column'} width={'100%'} gyMobile={16}>
            <FlexBox gxMobile={10}>
                <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width={'calc(50vw - 15px)'}
                    height={'calc(50vw - 15px)'}
                />
                <FlexBox gyMobile={4} directionMobile={'column'} >
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={'calc(50vw - 15px)'}
                        height={'7vw'}
                    />
                    <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={'calc(25vw - 15px)'}
                        height={'7vw'}
                    />
                </FlexBox>
            </FlexBox>
            <Skeleton
                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                width={'calc(83vw - 10px)'}
                height={'4.3vw'}
            />
            <FlexBox direction={'column'} offsetTop={8} width={'100%'}>
                <ProjectsGridNew isLoading={true} />
            </FlexBox>
        </FlexBox>
    }

    return (
        <FlexBox direction={'column'} directionMobile={'column'} width={'100%'}>
            {showMerge && props.mergeAccount && <MergeAccountsMsgWrapper>
                <span>
                    {t('designer_edit.merge_accounts_message')} <a href={props.mergeAccount} id="merge-button">{t('designer_edit.merge_accounts_here')}</a>. 
                    {' '}
                    {t('designer_edit.merge_accounts_later')}
                </span>
                <CloseButton onClick={() => setShowMerge(false)}>
                    <CloseIcon />
                </CloseButton>
            </MergeAccountsMsgWrapper>}
            <FlexBox className='bfr-cta'>
                <AvatarContainer>
                    {props.isLoading ? (
                        <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={270}
                            height={270}
                        />
                    ) : (
                        props.avatar
                    )}
                    {props.more}
                    {props.isLoading ? (
                        !isMobile && <FlexBox offsetTopMobile={16}>
                        </FlexBox>
                    ) : (
                        props.isAlumni
                    )}
                </AvatarContainer>
                {(!props.isLoading || !isMobile) && <FlexBox direction={'column'} width={'100%'} style={{ minHeight: is743? '270px' : 'auto', position: 'relative' }}>
                    <FlexBox style={{ minHeight: is743 ? '50px' : 'auto', position: 'relative' }} align={'flex-start'} justify={'space-between'}>
                        <FlexBox justify={'space-between'} justifyMobile='space-between' width={'100%'}>
                            {props.isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={294}
                                    height={50}
                                />
                            ) : (
                                props.title
                            )}
                            {props.buttons}
                        </FlexBox>
                        <FlexBox style={{ position: 'absolute', top: 0, right: 0 }}>
                            {props.isLoading ? (
                                <FlexBox gx={8}>
                                </FlexBox>
                            ) : props.social
                            }
                        </FlexBox>
                    </FlexBox>
                    {!props.isHiddenStatus && (
                        <FlexBox align={'center'} offsetTop={8}>
                            {props.isLoading ? (
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={270}
                                    height={22}
                                />
                            ) : (
                                props.status
                            )}
                        </FlexBox>
                    )}
                    {props.isLoading || props.isLoadingContent ? null : <FlexBox>{props?.subscribe}</FlexBox>}
                    {!isTab && <AboutTextWrapper isLoading={props.isLoading}>
                        {props.isLoading ? (
                            <FlexBox direction={'column'} width={'100%'}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={'100%'}
                                    height={112}
                                />
                                <FlexBox gx={24} offsetTop={32}>
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                    <Skeleton
                                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                        width={104}
                                        height={22}
                                    />
                                </FlexBox>
                            </FlexBox>
                        ) : (
                            props.description
                        )}
                    </AboutTextWrapper>}
                    {props.isLoading ? (
                        <FlexBox>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={'100%'}
                                height={112}
                            />
                        </FlexBox>
                    ) : (
                        props.channelInfo
                    )}
                    {<FlexBox align={'flex-end'} justify={'flex-start'} width={'100%'} height={'100%'}>
                        <FlexBox offsetTop={0} width="100%">
                            {props.isLoading ? (
                                <FlexBox gx={24}>
                                </FlexBox>
                            ) : (
                                <FlexBox className='designer-layout_netwroks' offsetTop={8} align='center' width="100%">
                                    {props.networks}
                                </FlexBox>
                            )}
                        </FlexBox>

                    </FlexBox>}
                </FlexBox>}


            </FlexBox>

            {isTab &&
                <AboutTextWrapper isLoading={props.isLoading}>
                    {props.isLoading ? (
                        <FlexBox direction={'column'} width={'100%'}>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={'100%'}
                                height={112}
                            />
                            <FlexBox gx={24} offsetTop={32}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={104}
                                    height={22}
                                />
                            </FlexBox>
                        </FlexBox>
                    ) : (
                        props.description
                    )}
                </AboutTextWrapper>
            }

            {props.ctaBlock}

            <FlexBox direction={'column'} width={'100%'} offsetTop={24}>
                <FlexBox width={'100%'} offsetTop={0}>
                    {props.isLoading ? (
                        <FlexBox offsetBottom={24} justify={'space-between'} width={'100%'}>
                            <FlexBox gx={32}>
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                                <Skeleton
                                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                    width={115}
                                    height={38}
                                />
                            </FlexBox>
                            <Skeleton
                                style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                                width={115}
                                height={38}
                            />
                        </FlexBox>
                    ) : (
                        props.line
                    )}
                </FlexBox>
                {!props?.noProjects?.tab && (
                    <>
                        <FlexBox width={'100%'}>{props.filterBar}</FlexBox>
                        <FlexBox width={'100%'}>{props.folders}</FlexBox>
                        <FlexBox width={'100%'} offsetBottom={props.isLoadingContent ? 24 : 0}>{props.cloudTags}</FlexBox>
                    </>
                )}
                <ContentHolder className='designer-layout_content-holder' direction={'column'} width={'100%'}>
                    {!props.isLoading && props.sortLine}
                    {props.isLoading || props.isLoadingContent && <ProjectsGridNew isLoading />}
                    {!props.isLoadingContent && (
                        <ProjectControllerWrapper>
                            {props.content}
                        </ProjectControllerWrapper>
                    )}
                </ContentHolder>
            </FlexBox>

        </FlexBox>
    );
};
